import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Upload, Hash, Trash2 } from "lucide-react";

// ── Hashing ──────────────────────────────────────────────────

type Algorithm = "SHA-256" | "SHA-384" | "SHA-512" | "SHA-1";

const ALGORITHMS: Algorithm[] = ["SHA-256", "SHA-384", "SHA-512", "SHA-1"];

async function hashText(text: string, algo: Algorithm): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hash = await crypto.subtle.digest(algo, data);
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

async function hashFile(file: File, algo: Algorithm): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hash = await crypto.subtle.digest(algo, buffer);
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

// ── Component ────────────────────────────────────────────────

export function HashGenerator() {
    const [input, setInput] = useState("");
    const [algorithm, setAlgorithm] = useState<Algorithm>("SHA-256");
    const [hashes, setHashes] = useState<Record<Algorithm, string>>({
        "SHA-256": "",
        "SHA-384": "",
        "SHA-512": "",
        "SHA-1": "",
    });
    const [copied, setCopied] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [uppercase, setUppercase] = useState(false);

    useEffect(() => {
        document.title =
            "Hash Generator — SHA-256, SHA-512 & More — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Generate SHA-256, SHA-384, SHA-512, and SHA-1 hashes from text or files. Free online hash generator tool.",
            );
        }
    }, []);

    const computeHashes = useCallback(async () => {
        if (!input.trim()) {
            setHashes({ "SHA-256": "", "SHA-384": "", "SHA-512": "", "SHA-1": "" });
            return;
        }
        const results: Record<string, string> = {};
        for (const algo of ALGORITHMS) {
            results[algo] = await hashText(input, algo);
        }
        setHashes(results as Record<Algorithm, string>);
    }, [input]);

    useEffect(() => {
        const timer = setTimeout(computeHashes, 100);
        return () => clearTimeout(timer);
    }, [computeHashes]);

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        const results: Record<string, string> = {};
        for (const algo of ALGORITHMS) {
            results[algo] = await hashFile(file, algo);
        }
        setHashes(results as Record<Algorithm, string>);
        setInput(`[File: ${file.name} — ${(file.size / 1024).toFixed(1)} KB]`);
        e.target.value = "";
    };

    const copy = async (val: string, key: string) => {
        await navigator.clipboard.writeText(uppercase ? val.toUpperCase() : val);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">ballah code</Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">Hash Generator</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">Hash Generator</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Generate cryptographic hashes from text or files using SHA-256, SHA-384, SHA-512, and SHA-1.
                    </p>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <label className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-500 hover:text-zinc-300 hover:border-white/[0.1] cursor-pointer transition-colors">
                        <Upload size={12} />
                        Upload file
                        <input type="file" onChange={handleFile} className="hidden" />
                    </label>
                    {fileName && <span className="text-[12px] text-zinc-600">{fileName}</span>}

                    <label className="flex items-center gap-2 text-[12px] text-zinc-500 cursor-pointer ml-auto">
                        <input
                            type="checkbox"
                            checked={uppercase}
                            onChange={(e) => setUppercase(e.target.checked)}
                            className="rounded border-white/[0.1] bg-transparent"
                        />
                        Uppercase
                    </label>

                    {input && (
                        <button onClick={() => { setInput(""); setFileName(null); }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-600 hover:text-zinc-400 transition-colors">
                            <Trash2 size={12} /> Clear
                        </button>
                    )}
                </div>

                {/* Input */}
                <div className="mb-6">
                    <div className="px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                        <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Input text</span>
                    </div>
                    <textarea
                        value={input}
                        onChange={(e) => { setInput(e.target.value); setFileName(null); }}
                        placeholder="Type or paste text to hash..."
                        spellCheck={false}
                        className="w-full min-h-[150px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                    />
                </div>

                {/* Hash outputs */}
                {input.trim() && (
                    <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden mb-16">
                        {ALGORITHMS.map((algo, i) => (
                            <div key={algo} className={`px-4 py-3 ${i < ALGORITHMS.length - 1 ? "border-b border-white/[0.04]" : ""}`}>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className={`text-[11px] font-mono uppercase tracking-wider ${algorithm === algo ? "text-violet-400" : "text-zinc-600"}`}>
                                        <Hash size={10} className="inline mr-1" />{algo}
                                    </span>
                                    <button onClick={() => copy(hashes[algo]!, algo)} className="text-zinc-700 hover:text-zinc-300 transition-colors">
                                        {copied === algo ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}
                                    </button>
                                </div>
                                <code className="text-[12px] text-zinc-400 font-mono break-all leading-relaxed">
                                    {uppercase ? hashes[algo]!.toUpperCase() : hashes[algo]}
                                </code>
                            </div>
                        ))}
                    </div>
                )}

                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">What are cryptographic hash functions?</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>A cryptographic hash function takes an input and produces a fixed-size string of bytes. The output (digest) is unique to each unique input — even a tiny change produces a completely different hash.</p>
                        <p>SHA-256 is the most widely used hash today, common in Git commits, blockchain, and data integrity checks. SHA-1 is considered weak and should not be used for security purposes, but is still used in some legacy systems.</p>
                        <p>Everything runs in your browser using the Web Crypto API. Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/base64" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Base64 Encode / Decode</Link>
                        <Link to="/tools/jwt-encoder" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JWT Encoder</Link>
                        <Link to="/tools/uuid-generator" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">UUID Generator</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
